import React, { useEffect, useRef, useState } from 'react'

import { Box, Button, Typography } from '@material-ui/core'
import TextField from '@material-ui/core/TextField'

import { LoadingFeedback } from 'components'
import useTrainingJourney from 'hooks/useTrainingJourney'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'
import * as service from 'service'
import useStyles from './styles'

import courseAccessConstants from 'constants/courseAccess'

const VerifyCodeStep = () => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const inputRefs = useRef([])

  const { DIGITS_COUNT, RESEND_SECONDS } = courseAccessConstants

  const [isLoading, setIsLoading] = useState(false)
  const [digits, setDigits] = useState(Array(DIGITS_COUNT).fill(''))
  const [countdown, setCountdown] = useState(RESEND_SECONDS)

  const { accessType, company, formData, portalToken, setActiveStep } =
    useTrainingJourney()

  const isPhoneCode = !!formData?.phone

  useEffect(() => {
    if (countdown <= 0) return
    const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  const handleDigitChange = (index, value) => {
    const cleaned = value.replace(/\D/g, '').slice(-1)
    const next = [...digits]
    next[index] = cleaned
    setDigits(next)
    if (cleaned && index < DIGITS_COUNT - 1) {
      if (inputRefs.current[index + 1]) inputRefs.current[index + 1].focus()
    }
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      if (inputRefs.current[index - 1]) inputRefs.current[index - 1].focus()
    }
  }

  const handlePaste = (event) => {
    event.preventDefault()
    const cleaned = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, DIGITS_COUNT)
    if (!cleaned) return
    const next = Array(DIGITS_COUNT).fill('')
    cleaned.split('').forEach((char, index) => {
      next[index] = char
    })
    setDigits(next)
    const focusIndex = Math.min(cleaned.length, DIGITS_COUNT - 1)
    if (inputRefs.current[focusIndex]) inputRefs.current[focusIndex].focus()
  }

  const handleResend = async () => {
    setIsLoading(true)
    try {
      await service.trainingPortal.auth.requestCode({
        companyId: company?.id,
        ...(isPhoneCode
          ? { phone: formData?.phone }
          : { email: formData?.email }),
        accessType,
      })
      setCountdown(RESEND_SECONDS)
      snackbar.open({
        message: `Código reenviado. Verifique seu ${
          isPhoneCode ? 'telefone' : 'e-mail'
        }.`,
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao reenviar o código. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async () => {
    const code = digits.join('')
    if (code.length < DIGITS_COUNT) return
    setIsLoading(true)
    try {
      const response = await service.trainingPortal.auth.verifyCode({
        companyId: company?.id,
        code,
        ...(isPhoneCode
          ? { phone: formData?.phone }
          : { email: formData?.email }),
      })
      service.trainingPortal.auth.setToken(response?.data?.token)
      service.history.push(`/training/${portalToken}/home`)
    } catch (error) {
      const status = error?.response?.status
      const message = helpers.formatters.errorMessage(
        error?.response?.data?.error,
      )
      if (
        status === 401 &&
        (message?.includes('Sessão') || message?.includes('expirado'))
      ) {
        snackbar.open({ message, variant: 'error' })
        setActiveStep(0)
        return
      }
      snackbar.open({
        message: message || 'Código inválido. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const isComplete = digits.every((digit) => digit !== '')

  useEffect(() => {
    if (isComplete) handleSubmit()
    // eslint-disable-next-line
  }, [digits])

  return (
    <Box display="flex" flexDirection="column">
      <LoadingFeedback open={isLoading} />
      <Typography color="primary" className={classes.title}>
        Duplo Fator de Autenticação
      </Typography>
      <Typography className={classes.subtitle}>
        Para sua segurança, enviamos um código de acesso ao seu
        {isPhoneCode ? ' telefone.' : ' e-mail.'}
      </Typography>
      <Box className={classes.infoBox}>
        <Typography className={classes.infoLabel}>
          Enviamos um código de verificação no seu
          {isPhoneCode ? ' telefone' : ' e-mail'}
        </Typography>
        <Typography className={classes.emailText}>
          {isPhoneCode ? formData?.phone : formData?.email}
        </Typography>
        <Box className={classes.digitsRow}>
          {digits.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(element) => {
                inputRefs.current[index] = element
              }}
              value={digit}
              onChange={(event) => handleDigitChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onPaste={handlePaste}
              variant="outlined"
              inputProps={{ maxLength: 1 }}
              className={classes.digitInput}
            />
          ))}
        </Box>

        <Typography className={classes.resendRow}>
          {countdown > 0 ? (
            <>
              Não recebeu seu código?{' '}
              <strong>Reenvie em {countdown} segundos</strong>
            </>
          ) : (
            <>
              Não recebeu seu código?{' '}
              <button className={classes.resendButton} onClick={handleResend}>
                Reenviar agora
              </button>
            </>
          )}
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        className={classes.submitButton}
        onClick={handleSubmit}
        disabled={!isComplete || isLoading}
      >
        AVANÇAR
      </Button>
    </Box>
  )
}

export default VerifyCodeStep
